/**
 * JAISIU-3503 / processing-transparency — network-blip toast reducer.
 *
 * When the gateway connection flaps (WS drop, request timeout, gateway
 * 5xx on a retryable method), the host surfaces a non-blocking toast
 * with:
 *   - the kind of blip ("Stream interrupted", "Connection lost", …)
 *   - an automatic retry counter (3s → 6s → 12s exponential, capped)
 *   - a manual override ("Retry now" / "Dismiss")
 *
 * The reducer is pure: callers pass in a `NetworkBlipAction` and the
 * current state, get back the next state. The host owns the timer
 * (via `setTimeout` in app.ts) so this file stays framework-free and
 * unit-testable.
 *
 * The toast does NOT block the run. While it's up, the live activity
 * pill still ticks; once the gateway re-responds, the toast auto-dismisses
 * and the run resumes from the runId. The operator can also "Retry now"
 * to fire the reconnect immediately, or "Dismiss" to silence the toast
 * without affecting the underlying reconnect.
 */

export type NetworkBlipKind =
  /** Stream events stopped arriving while the run is still active. */
  | "stream_stalled"
  /** WS / HTTP layer dropped (reconnect handshake pending). */
  | "connection_lost"
  /** Gateway returned 5xx or a retryable transport error. */
  | "gateway_retryable"
  /** Catch-all for any retryable error that doesn't fit the above. */
  | "transient";

export type NetworkBlipState = {
  /** Stable id so Lit's repeat can diff blips across renders. */
  id: string;
  kind: NetworkBlipKind;
  /** Short headline for the toast. */
  title: string;
  /** One-line detail; safe for aria-live announcement. */
  detail: string;
  /** Number of auto-retries already attempted for this blip. */
  attempt: number;
  /** Wall-clock ms when the next auto-retry fires (Date.now() base). */
  nextRetryAt: number;
  /** Backoff in ms between auto-retries (doubles each attempt, capped). */
  backoffMs: number;
  /** True while the auto-retry timer is armed. */
  autoRetryArmed: boolean;
  /** Set when the operator hit "Retry now" — host should fire immediately. */
  manualRetryRequested: boolean;
  /** True after the host acknowledges the blip is resolved. */
  resolved: boolean;
  /** Set when the operator hit "Dismiss" — the toast hides but the host
   *  may continue the auto-retry silently. */
  dismissed: boolean;
};

const MAX_ATTEMPTS = 5;
const INITIAL_BACKOFF_MS = 3_000;
const MAX_BACKOFF_MS = 30_000;

const KIND_COPY: Readonly<Record<NetworkBlipKind, { title: string; detail: string }>> = {
  stream_stalled: {
    title: "Stream stalled",
    detail: "Waiting for the gateway to resume streaming — reconnecting\u2026",
  },
  connection_lost: {
    title: "Connection lost",
    detail: "Reconnecting to the gateway in the background\u2026",
  },
  gateway_retryable: {
    title: "Gateway hiccup",
    detail: "Retrying the last request automatically\u2026",
  },
  transient: {
    title: "Network blip",
    detail: "Reconnecting\u2026",
  },
};

let _idCounter = 0;
function nextBlipId(): string {
  _idCounter = (_idCounter + 1) | 0;
  return `blip-${Date.now().toString(36)}-${_idCounter.toString(36)}`;
}

export type NetworkBlipAction =
  | { type: "open"; kind: NetworkBlipKind; at?: number }
  | { type: "tick"; at: number }
  | { type: "retry_now" }
  | { type: "retry_scheduled"; at: number }
  | { type: "resolve" }
  | { type: "dismiss" };

/** Initial state when no blip is active. */
export const NO_BLIP: NetworkBlipState | null = null;

/** Compute the next backoff in ms given the attempt count (0-based). */
export function computeBackoffMs(attempt: number): number {
  if (attempt < 0) {
    return INITIAL_BACKOFF_MS;
  }
  const exp = Math.min(MAX_BACKOFF_MS, INITIAL_BACKOFF_MS * 2 ** attempt);
  // Jitter ±20% so simultaneous blips don't synchronize.
  const jitter = Math.floor(exp * (Math.random() * 0.4 - 0.2));
  return Math.max(500, exp + jitter);
}

/**
 * Reduce a network blip action. Returns the next state — either a
 * `NetworkBlipState` (blip is active) or `null` (no blip).
 *
 * Pure: does not read or write any external state. The host owns the
 * timer and fires `tick` / `retry_scheduled` / `resolve` actions from
 * its `setTimeout` callbacks and gateway event handlers.
 */
export function reduceNetworkBlip(
  state: NetworkBlipState | null,
  action: NetworkBlipAction,
): NetworkBlipState | null {
  switch (action.type) {
    case "open": {
      // If a blip is already open for the same kind, don't stack — the
      // existing toast just gets a bumped attempt count and reset timer.
      if (state && !state.resolved && !state.dismissed && state.kind === action.kind) {
        const nextAttempt = state.attempt + 1;
        return {
          ...state,
          attempt: nextAttempt,
          backoffMs: computeBackoffMs(nextAttempt),
          nextRetryAt: (action.at ?? Date.now()) + computeBackoffMs(nextAttempt),
          autoRetryArmed: nextAttempt < MAX_ATTEMPTS,
          manualRetryRequested: false,
        };
      }
      const at = action.at ?? Date.now();
      const backoff = computeBackoffMs(0);
      return {
        id: nextBlipId(),
        kind: action.kind,
        title: KIND_COPY[action.kind].title,
        detail: KIND_COPY[action.kind].detail,
        attempt: 0,
        nextRetryAt: at + backoff,
        backoffMs: backoff,
        autoRetryArmed: true,
        manualRetryRequested: false,
        resolved: false,
        dismissed: false,
      };
    }
    case "tick": {
      if (!state || state.resolved || state.dismissed) {
        return state;
      }
      if (action.at < state.nextRetryAt) {
        return state;
      }
      // Time to auto-retry — flip the flag and let the host's tick
      // callback fire the actual reconnect. The host will follow up
      // with `retry_scheduled` (retry started) or `resolve` (recovered).
      return state;
    }
    case "retry_now": {
      if (!state || state.resolved || state.dismissed) {
        return state;
      }
      return {
        ...state,
        manualRetryRequested: true,
        autoRetryArmed: false,
      };
    }
    case "retry_scheduled": {
      if (!state || state.resolved || state.dismissed) {
        return state;
      }
      // The host acknowledged the retry fired; bump the attempt count
      // and schedule the next one.
      const nextAttempt = state.attempt + 1;
      return {
        ...state,
        manualRetryRequested: false,
        attempt: nextAttempt,
        backoffMs: computeBackoffMs(nextAttempt),
        nextRetryAt: (action.at ?? Date.now()) + computeBackoffMs(nextAttempt),
        autoRetryArmed: nextAttempt < MAX_ATTEMPTS,
      };
    }
    case "resolve": {
      if (!state) {
        return null;
      }
      return { ...state, resolved: true, autoRetryArmed: false };
    }
    case "dismiss": {
      if (!state) {
        return null;
      }
      return { ...state, dismissed: true, autoRetryArmed: false };
    }
    default:
      return state;
  }
}

/** True while a blip is still operator-visible (not resolved, not dismissed). */
export function isBlipVisible(state: NetworkBlipState | null): state is NetworkBlipState {
  return state != null && !state.resolved && !state.dismissed;
}

/** Seconds until the next auto-retry, formatted for the countdown label. */
export function formatCountdownSeconds(state: NetworkBlipState, now: number): number {
  const ms = Math.max(0, state.nextRetryAt - now);
  return Math.ceil(ms / 1000);
}
